import { Fragment, ReactNode } from "react";

import {
    Box,
    Skeleton,
    SkeletonProps,
    Tooltip as MuiTooltip,
    TooltipProps,
    TablePagination,
    useMediaQuery,
    SxProps
} from "@mui/material";
import { InboxOutlined, TravelExploreOutlined } from "@mui/icons-material";
import { FormInputType } from "@bridgesplit/react";
import { formatPercent } from "@bridgesplit/utils";

import { Image } from "./images";
import { Button, ConditionalLink, ButtonProps, TextExternalLink, IconButton } from "./buttons";
import { AbsoluteCenterWrapper, Column, Row, ShadowCard } from "./containers";
import { Text, TextColor, TextVariant, Link, TooltipText, Span } from "./typography";
import { CARD_WIDTH } from "../constants";
import { SxType } from "../types";
import {
    BORDER_RADIUS,
    BORDER_RADIUS_SM,
    INPUT_HEIGHT,
    useAppPalette,
    FONT_WEIGHTS,
    LIGHT_SMALL_SHADOW,
    BRAND_COLORS
} from "../theme";
import { Icon, IconHeader, IconWithBackground, Dot } from "./icons";
import { MEDIA } from "../util";
import { ProgressBar } from "./info";

export interface CustomTooltipProps extends TooltipProps {
    fullWidth?: boolean;
    containerSx?: SxType;
    reverseColors?: boolean;
    minTooltipWidth?: string;
    noPadding?: boolean;
    tooltipSx?: SxType;
}

export const Tooltip = ({
    className,
    containerSx,
    reverseColors,
    fullWidth,
    placement,
    minTooltipWidth,
    noPadding,
    tooltipSx,
    ...props
}: CustomTooltipProps) => {
    const { primary, divider, cardBackground, background } = useAppPalette();
    const isMobile = useMediaQuery(MEDIA.MD.below);
    const tootlipIsNode = props.title !== null && typeof props.title != "string";

    return (
        <MuiTooltip
            slotProps={{
                tooltip: {
                    sx: {
                        color: reverseColors ? primary : background,
                        backgroundColor: reverseColors ? background : primary,
                        border: reverseColors ? `1px solid ${divider}` : undefined,
                        minWidth: minTooltipWidth ?? undefined,
                        padding: noPadding ? 0 : undefined,
                        borderRadius: tootlipIsNode ? BORDER_RADIUS : BORDER_RADIUS_SM,
                        boxShadow: LIGHT_SMALL_SHADOW,
                        ...tooltipSx
                    }
                }
            }}
            {...props}
            placement={isMobile ? "bottom" : placement}
            enterTouchDelay={300}
            arrow={false}
            classes={{ popper: className }}
            sx={{ backgroundColor: reverseColors ? cardBackground : undefined }}
        >
            <Box
                sx={{
                    width: fullWidth ? "100%" : "fit-content",
                    ...containerSx
                }}
            >
                {props.children}
            </Box>
        </MuiTooltip>
    );
};

export const SkeletonRounded = (props: SkeletonProps) => (
    <Skeleton {...props} variant="rectangular" sx={{ borderRadius: BORDER_RADIUS_SM, ...props.sx }} />
);

export const ButtonSkeleton = (props: SkeletonProps) => (
    <Skeleton
        {...props}
        variant="rectangular"
        sx={{ borderRadius: BORDER_RADIUS, height: INPUT_HEIGHT + "px", width: "100%", ...props.sx }}
    />
);

export function repeatElement(node: ReactNode, length = 5) {
    return Array(length)
        .fill(null)
        .map((_, i) => <Fragment key={i}> {node} </Fragment>);
}

export function Error404Page() {
    return (
        <AbsoluteCenterWrapper>
            <ShadowCard sx={{ width: CARD_WIDTH }} padding>
                <NotFoundCard header="Page not found" description="We can’t find the page you are looking for" />
            </ShadowCard>
        </AbsoluteCenterWrapper>
    );
}

export function NotFoundCard({ header, description }: { header: string; description: string }) {
    return (
        <Column sx={{ width: "100%", alignItems: "center", textAlign: "center" }} spacing={2}>
            <IconHeader>
                <TravelExploreOutlined />
            </IconHeader>
            <Column sx={{ alignItems: "center" }}>
                <Text variant="h3">{header}</Text>
                <Text color="caption" variant="body2">
                    {description}
                </Text>
            </Column>
            <Link sx={{ width: "100%" }} to="/">
                <Button variant="contained" fullWidth>
                    Back home
                </Button>
            </Link>
        </Column>
    );
}

type PromptCtaType = { text: ReactNode; to?: string; onClick?: () => void; buttonProps?: ButtonProps };
export type EmptyPlaceholderPropsWithCta = {
    cta?: PromptCtaType | PromptCtaType[];
    header: string;
    description?: string;
    icon?: JSX.Element;
};
export type EmptyPlaceholderProps = EmptyPlaceholderPropsWithCta;

export function EmptyPlaceholder(props: EmptyPlaceholderProps) {
    const { icon = <InboxOutlined />, header, description, cta } = props;
    return (
        <Column
            spacing={3}
            sx={{
                height: "100%",
                mt: 2,
                px: 2,
                pb: 2,
                textAlign: "center",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            <Column spacing={2} sx={{ maxWidth: "500px", alignItems: "center", justifyContent: "center" }}>
                <IconWithBackground variant="h1" size={50}>
                    {icon}
                </IconWithBackground>

                <Column alignItems="center" spacing={0.5}>
                    <Text variant="h4" fontWeight="bold">
                        {header}
                    </Text>
                    {!!description && (
                        <Text variant="body2" color="caption">
                            {description}
                        </Text>
                    )}
                </Column>
                {cta && !Array.isArray(cta) && <PromptCta cta={cta} />}
                {cta && Array.isArray(cta) && (
                    <Row spacing={1}>
                        {cta.map((c, i) => (
                            <PromptCta variant={i === cta.length - 1 ? "contained" : "outlined"} key={i} cta={c} />
                        ))}
                    </Row>
                )}
            </Column>
        </Column>
    );
}

function PromptCta({ cta, variant = "contained" }: { cta: PromptCtaType; variant?: "contained" | "outlined" }) {
    return (
        <ConditionalLink to={cta.to}>
            <Button {...cta.buttonProps} variant={variant} onClick={cta.onClick}>
                {cta.text}
            </Button>
        </ConditionalLink>
    );
}

export interface CheckListProps {
    icon: ReactNode;
    title: string;
    description: string;
    completed: boolean;
    cta?: (() => void) | string; // string if it's an internal path
    ctaText?: string;
    docsLink?: string;
    disabled?: boolean;
    buttonProps?: ButtonProps;
    key?: string | number; // by default title is used as key
}

export function CheckListItem({
    item,
    active,
    setActive
}: {
    item: CheckListProps | undefined;
    active: boolean;
    setActive: () => void;
}) {
    const { border, hoverBackground } = useAppPalette();
    const clickable = !item?.disabled && !active;
    return (
        <Column
            onClick={item?.disabled ? undefined : setActive}
            sx={{
                cursor: clickable ? "pointer" : "default",
                opacity: active ? 1 : 0.5,
                border,
                borderRadius: BORDER_RADIUS,
                p: 2,
                ":hover": { background: clickable ? hoverBackground : undefined }
            }}
            spacing={2}
        >
            <Row spaceBetween>
                <Row spacing={1}>
                    {!item ? (
                        <Image size="30px" variant="circle" src={undefined} />
                    ) : (
                        <Text sx={{ width: "30px" }} variant="h1">
                            {item?.icon}
                        </Text>
                    )}
                    <Column>
                        <Row spacing={0.5}>
                            <Text variant="h4" loading={!item}>
                                {item?.title}
                            </Text>
                            {item?.completed && (
                                <TooltipText helpText="Complete" icon={false} color="success">
                                    <Icon type="accept" />
                                </TooltipText>
                            )}
                        </Row>

                        <Text color="caption" variant="body2" loading={!item}>
                            {item?.description}
                        </Text>
                    </Column>
                </Row>
                {!active && <IconButton icon="more" />}
            </Row>

            {active && (
                <Row spacing={1} sx={{ pl: 4 }}>
                    {item?.cta && (
                        <ConditionalLink to={typeof item?.cta === "string" ? item.cta : undefined}>
                            <Button
                                {...item?.buttonProps}
                                variant="contained"
                                onClick={typeof item?.cta === "string" ? undefined : item?.cta}
                            >
                                {item?.ctaText}
                            </Button>
                        </ConditionalLink>
                    )}
                    {item?.docsLink && (
                        <Button {...item.buttonProps} variant="outlined" onClick={() => window.open(item.docsLink)}>
                            View docs
                        </Button>
                    )}
                </Row>
            )}
        </Column>
    );
}

export function Pagination({
    count,
    page,
    setPage,
    rowsPerPage,
    disabled
}: {
    page: number;
    setPage: FormInputType<number>;
    count: number;
    rowsPerPage: number;
    disabled?: boolean;
}) {
    return (
        <TablePagination
            sx={{ py: 0, minHeight: "fit-content", overflow: "visible" }}
            labelDisplayedRows={({ page, count }) => (count ? `${page + 1} of ${Math.ceil(count / rowsPerPage)}` : "")}
            rowsPerPageOptions={[rowsPerPage]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => {
                setPage(newPage);
            }}
            disabled={disabled}
        />
    );
}

export const dottedUnderline = (): SxProps => ({
    textDecoration: "underline",
    textDecorationStyle: "dotted",
    textDecorationColor: "divider",
    textDecorationThickness: "1px",
    textUnderlineOffset: "4px"
});

interface StatWithIconProps {
    value: string | number | undefined;
    variant?: TextVariant;
    captionColor?: TextColor;
    icon?: React.ReactNode;
    loading?: boolean;
}
export function StatWithIcon({
    value,
    variant = "body1",
    captionColor = "caption",
    icon,
    loading
}: StatWithIconProps): JSX.Element {
    return (
        <Row spacing={1} sx={{ width: "100px", justifyContent: "flex-end" }}>
            {icon}
            <Column>
                <Text variant={variant} color={captionColor} loading={loading}>
                    {value !== undefined ? value : ""}
                </Text>
            </Column>
        </Row>
    );
}

export function RewardIcon({
    hasMultiplier,
    isNotEarningPoints
}: {
    hasMultiplier?: boolean;
    isNotEarningPoints?: boolean;
}) {
    const { prime, textDisabled, secondary } = useAppPalette();

    const color = isNotEarningPoints ? textDisabled : hasMultiplier ? prime : secondary;
    return (
        <Text
            sx={{
                color: color
            }}
        >
            <Icon type="points" />
        </Text>
    );
}

export function TextWithLink({
    text,
    link,
    isError
}: {
    text: string | undefined;
    link: string | undefined;
    isError: boolean;
}) {
    return text ? (
        <>
            {text + " "}
            {link && (
                <TextExternalLink
                    sx={{ color: isError ? "warning" : "secondary", fontWeight: FONT_WEIGHTS.semibold }}
                    href={link}
                >
                    Learn more
                    <Icon sx={{ pl: 0.25, pb: 0.25, color: isError ? "warning" : "secondary" }} type="external-link" />
                </TextExternalLink>
            )}
        </>
    ) : null;
}

const MAX_BAR_VALUE = 100;

export interface LtvHealthBarProps {
    health: number | undefined;
    ltv?: number;
    liquidationThreshold?: number;
    healthColor: string;
    variant?: TextVariant;
    simple?: boolean;
}

export function LtvHealthBar({
    health,
    ltv,
    liquidationThreshold,
    healthColor,
    variant = "body1",
    simple = false
}: LtvHealthBarProps) {
    if (simple) {
        return <HealthStatRow health={health} variant={variant} healthColor={healthColor} />;
    }

    if (health === undefined || ltv === undefined || liquidationThreshold === undefined) return null;

    const barValue = Math.min(health * MAX_BAR_VALUE, MAX_BAR_VALUE);

    return (
        <Column spacing={0.25} sx={{ minWidth: "110px" }}>
            <HealthStatRow health={health} variant={variant} healthColor={healthColor} />
            <ProgressBar value={barValue} bgColor={healthColor} sx={{ height: "4px" }} />
            <Row spacing={1} sx={{ alignItems: "center", mt: 0.5 }}>
                <Text variant="caption" color="caption">
                    <Span>LTV: {formatPercent(ltv)}</Span>
                </Text>
                <Dot color={BRAND_COLORS.GRAY} size={4} />
                <Text variant="caption" color="caption">
                    <Span>Liq: {formatPercent(liquidationThreshold)}</Span>
                </Text>
            </Row>
        </Column>
    );
}

function HealthStatRow({
    health,
    variant,
    healthColor
}: {
    health: number | undefined;
    variant: TextVariant;
    healthColor: string;
}) {
    return (
        <Row spacing={0.5} sx={{ alignItems: "center" }}>
            <Text variant={variant} loading={health === undefined}>
                <Dot color={healthColor} /> {formatPercent(health)}
            </Text>
        </Row>
    );
}
